import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import { aplicarTemaCompleto, limparTema, obterIniciais, useTenantTheme } from "../utils/theme";
import { linkSuporteWhatsApp } from "../utils/whatsappSupport";

function MenuLink({ to, icon, children, onNavigate }) {
  const location = useLocation();
  const active = location.pathname === to;

  return (
    <Link className={`submenu-link ${active ? "active" : ""}`} to={to} onClick={onNavigate}>
      <span className="menu-icon">{icon}</span>
      <span>{children}</span>
    </Link>
  );
}

function MenuGroup({ id, title, icon, aberto, onToggle, children }) {
  return (
    <div className="menu-group">
      <button
        type="button"
        className={`menu-group-button ${aberto ? "open" : ""}`}
        onClick={() => onToggle(id)}
      >
        <span className="menu-group-left">
          <span className="menu-icon">{icon}</span>
          <span>{title}</span>
        </span>

        <span className="menu-arrow">{aberto ? "▾" : "▸"}</span>
      </button>

      {aberto && <div className="submenu">{children}</div>}
    </div>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();

  const nome = localStorage.getItem("nome");
  const perfil = localStorage.getItem("perfil");
  const tema = useTenantTheme();
  const [menuMobileAberto, setMenuMobileAberto] = useState(false);
  const statusAssinatura = localStorage.getItem("statusAssinatura");
  const testeExpiraEm = localStorage.getItem("testeExpiraEm");
  const diasRestantesTeste = statusAssinatura === "Teste" && testeExpiraEm
    ? Math.max(0, Math.ceil((new Date(testeExpiraEm).getTime() - Date.now()) / 86400000))
    : null;

  const [menusAbertos, setMenusAbertos] = useState({
    atendimento: true,
    comercial: false,
    clientes: false,
    estoque: false,
    cadastros: false,
    fiscal: false,
    administracao: false
  });

  function toggleMenu(menu) {
    setMenusAbertos((prev) => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  }

  function sair() {
    const emailLembrado = localStorage.getItem("loginEmail");
    localStorage.clear();
    if (emailLembrado) {
      localStorage.setItem("loginEmail", emailLembrado);
    }
    limparTema();
    navigate("/login");
  }

  useEffect(() => {
    api.get("/tenant/tema")
      .then((res) => aplicarTemaCompleto(res.data))
      .catch(() => null);
  }, []);

  useEffect(() => {
    setMenuMobileAberto(false);
  }, [location.pathname]);

  const fecharMenuMobile = () => setMenuMobileAberto(false);
  const isAdmin = perfil === "Administrador";
  const isGerente = perfil === "Gerente";
  const isAtendente = perfil === "Atendente";
  const isProfissional = perfil === "Profissional";
  const isEstoque = perfil === "Estoque";

  return (
    <div className="app-shell">
      <div className="mobile-topbar">
        <button
          type="button"
          className="mobile-menu-button"
          onClick={() => setMenuMobileAberto(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>

        <div className="mobile-brand">
          {tema.logoExibicao
            ? <img className="mobile-brand-logo" src={tema.logoExibicao} alt={tema.nome} />
            : <span className="mobile-brand-logo brand-monogram">{obterIniciais(tema.nome)}</span>}
          <div>
            <strong>{tema.nome}</strong>
            <span>Gestao profissional</span>
          </div>
        </div>
      </div>

      {menuMobileAberto && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Fechar menu"
          onClick={fecharMenuMobile}
        />
      )}

      <aside className={`sidebar ${menuMobileAberto ? "open" : ""}`}>
        <button
          type="button"
          className="sidebar-close"
          onClick={fecharMenuMobile}
          aria-label="Fechar menu"
        >
          ×
        </button>

        <div className="brand">
          {tema.logoExibicao
            ? <img className="brand-logo" src={tema.logoExibicao} alt={tema.nome} />
            : <span className="brand-logo brand-monogram">{obterIniciais(tema.nome)}</span>}
          <div>
            <h4>{tema.nome}</h4>
            <small>Gestao profissional</small>
          </div>
        </div>

        <div className="user-box">
          <strong>{nome}</strong>
          <span>{perfil}</span>
        </div>

        <nav className="main-menu">
          {(isAdmin || isGerente) && (
            <MenuLink to="/" icon="📊" onNavigate={fecharMenuMobile}>
              Dashboard
            </MenuLink>
          )}

          {(isAdmin || isGerente || isAtendente || isProfissional) && (
            <MenuGroup
              id="atendimento"
              title="Atendimento"
              icon="💆"
              aberto={menusAbertos.atendimento}
              onToggle={toggleMenu}
            >
              <MenuLink to="/agenda" icon="📅" onNavigate={fecharMenuMobile}>Agenda</MenuLink>
              {(isAdmin || isGerente) && <MenuLink to="/agendamento-online" icon="🔗" onNavigate={fecharMenuMobile}>Agendamento online</MenuLink>}
              <MenuLink to="/atendimentos" icon="📝" onNavigate={fecharMenuMobile}>Fichas</MenuLink>
              <MenuLink to="/anamneses" icon="📋" onNavigate={fecharMenuMobile}>Anamnese</MenuLink>
              <MenuLink to="/prontuarios" icon="📋" onNavigate={fecharMenuMobile}>Prontuários</MenuLink>
              <MenuLink to="/termos" icon="📄" onNavigate={fecharMenuMobile}>Termos</MenuLink>
              <MenuLink to="/fotos-evolucao" icon="🖼️" onNavigate={fecharMenuMobile}>Fotos</MenuLink>
              <MenuLink to="/planos-tratamento" icon="🗂️" onNavigate={fecharMenuMobile}>Planos</MenuLink>
              <MenuLink to="/receitas" icon="🧾" onNavigate={fecharMenuMobile}>Receitas</MenuLink>
              <MenuLink to="/orcamentos" icon="💰" onNavigate={fecharMenuMobile}>Orçamentos</MenuLink>
              {isProfissional && <MenuLink to="/minhas-comissoes" icon="💰" onNavigate={fecharMenuMobile}>Minhas comissões</MenuLink>}
            </MenuGroup>
          )}

          {(isAdmin || isGerente || isAtendente) && (
            <MenuGroup
              id="comercial"
              title="Comercial / PDV"
              icon="🛒"
              aberto={menusAbertos.comercial}
              onToggle={toggleMenu}
            >
              <MenuLink to="/pdv" icon="🧾" onNavigate={fecharMenuMobile}>PDV</MenuLink>
              {(isAdmin || isGerente) && <MenuLink to="/vendas" icon="📋" onNavigate={fecharMenuMobile}>Vendas</MenuLink>}
              <MenuLink to="/caixa" icon="💵" onNavigate={fecharMenuMobile}>Caixa e fechamento</MenuLink>
              {(isAdmin || isGerente) && <MenuLink to="/pdv-terminais" icon="▦" onNavigate={fecharMenuMobile}>Terminais</MenuLink>}
              {(isAdmin || isGerente) && <MenuLink to="/financeiro" icon="📒" onNavigate={fecharMenuMobile}>Contas e fluxo</MenuLink>}
              {(isAdmin || isGerente) && <MenuLink to="/comissoes" icon="💰" onNavigate={fecharMenuMobile}>Comissões</MenuLink>}
              {(isAdmin || isGerente) && <MenuLink to="/fechamento-mensal" icon="📅" onNavigate={fecharMenuMobile}>Fechamento mensal</MenuLink>}
            </MenuGroup>
          )}

          {(isAdmin || isGerente || isAtendente) && (
            <MenuGroup
              id="clientes"
              title="Clientes"
              icon="👥"
              aberto={menusAbertos.clientes}
              onToggle={toggleMenu}
            >
              <MenuLink to="/clientes" icon="👤" onNavigate={fecharMenuMobile}>Cadastro de Clientes</MenuLink>
              <MenuLink to="/aniversariantes" icon="🎂" onNavigate={fecharMenuMobile}>Aniversariantes</MenuLink>
              <MenuLink to="/fidelidade" icon="🎁" onNavigate={fecharMenuMobile}>Fidelidade e planos</MenuLink>
              <MenuLink to="/marketing" icon="📣" onNavigate={fecharMenuMobile}>Marketing e retornos</MenuLink>
            </MenuGroup>
          )}

          {(isAdmin || isGerente || isEstoque) && (
            <MenuGroup
              id="estoque"
              title="Estoque"
              icon="📦"
              aberto={menusAbertos.estoque}
              onToggle={toggleMenu}
            >
              <MenuLink to="/produtos" icon="🧴" onNavigate={fecharMenuMobile}>Produtos</MenuLink>
              <MenuLink to="/estoque" icon="📦" onNavigate={fecharMenuMobile}>Movimentações</MenuLink>
              <MenuLink to="/estoque-lotes" icon="🏷️" onNavigate={fecharMenuMobile}>Lotes / Validade</MenuLink>
            </MenuGroup>
          )}

          {(isAdmin || isGerente) && (
            <MenuGroup
              id="cadastros"
              title="Cadastros"
              icon="⚙️"
              aberto={menusAbertos.cadastros}
              onToggle={toggleMenu}
            >
              <MenuLink to="/profissionais" icon="💇" onNavigate={fecharMenuMobile}>Profissionais</MenuLink>
              <MenuLink to="/servicos" icon="✨" onNavigate={fecharMenuMobile}>Serviços</MenuLink>
              <MenuLink to="/configuracao-clinica" icon="🏥" onNavigate={fecharMenuMobile}>Empresa e visual</MenuLink>
              {isAdmin && <MenuLink to="/unidades-vinculadas" icon="🏢" onNavigate={fecharMenuMobile}>Unidades vinculadas</MenuLink>}
            </MenuGroup>
          )}

          {(isAdmin || isGerente) && (
            <MenuGroup
              id="fiscal"
              title="Fiscal e Relatórios"
              icon="📑"
              aberto={menusAbertos.fiscal}
              onToggle={toggleMenu}
            >
              <MenuLink to="/notas-fiscais" icon="🧾" onNavigate={fecharMenuMobile}>Notas Fiscais</MenuLink>
              <MenuLink to="/relatorios" icon="📈" onNavigate={fecharMenuMobile}>Relatórios</MenuLink>
              <MenuLink to="/alertas" icon="⚠️" onNavigate={fecharMenuMobile}>Alertas</MenuLink>
              <MenuLink to="/financeiro-completo" icon="💳" onNavigate={fecharMenuMobile}>Financeiro</MenuLink>
              <MenuLink to="/mensalidades" icon="🧾" onNavigate={fecharMenuMobile}>Mensalidades do sistema</MenuLink>
              <MenuLink to="/inadimplentes" icon="⚠️" onNavigate={fecharMenuMobile}>Inadimplentes</MenuLink>
              <MenuLink to="/crm" icon="💬" onNavigate={fecharMenuMobile}>CRM</MenuLink>
              <MenuLink to="/configuracao-nfse" icon="🔧" onNavigate={fecharMenuMobile}>Configuração fiscal</MenuLink>
            </MenuGroup>
          )}

          {isAdmin && (
            <MenuGroup
              id="administracao"
              title="Administração"
              icon="🔐"
              aberto={menusAbertos.administracao}
              onToggle={toggleMenu}
            >
              <MenuLink to="/usuarios" icon="👨‍💻" onNavigate={fecharMenuMobile}>Usuários</MenuLink>
              <MenuLink to="/parametros-sistema" icon="⚙️" onNavigate={fecharMenuMobile}>Parâmetros do sistema</MenuLink>
              <MenuLink to="/administracao" icon="🏢" onNavigate={fecharMenuMobile}>Administração comercial</MenuLink>
              <MenuLink to="/auditoria" icon="📋" onNavigate={fecharMenuMobile}>Logs e auditoria</MenuLink>
              <MenuLink to="/lgpd" icon="🛡️" onNavigate={fecharMenuMobile}>LGPD</MenuLink>
            </MenuGroup>
          )}
        </nav>

        <div className="sidebar-footer-actions">
          <a className="btn btn-outline-light btn-sm support-button" href={linkSuporteWhatsApp()} target="_blank" rel="noreferrer">Suporte pelo WhatsApp</a>
          <button className="btn btn-light btn-sm logout-button" onClick={sair}>Sair do sistema</button>
        </div>
      </aside>

      <main className="content">
        {diasRestantesTeste !== null && (
          <div className="trial-status-banner" role="status">
            <strong>Período de teste</strong>
            <span>{diasRestantesTeste === 0 ? "Termina hoje" : `${diasRestantesTeste} ${diasRestantesTeste === 1 ? "dia restante" : "dias restantes"}`}</span>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
